import React, { ReactSVGElement, RefObject } from "react";
import { CloseFilled, ChevronDown } from "@carbon/icons-react";
import { Model } from "react-3layer-common";
import classNames from "classnames";
import { BORDER_TYPE } from "@Configs/enum";
import { InputAction } from "../InputText/InputText";
import "./InputSelect.scss";
import { Close } from "assets/icons";
import { Tooltip } from "antd";

export interface InputSelectProps<T extends Model> {
  /**User-selected value*/
  value?: T;
  /**Not allow to handle change value*/
  disabled?: boolean;
  /** Focus to input of inputSelect */
  expanded?: boolean;
  /** Set expand value */
  handlePressExpandedIcon?: () => void;
  /** Placeholder of the Component */
  placeHolder?: string;
  /**Provide a function to render a specific property as name*/
  render?: (t: T) => string;
  /**Handle the action when click clear value*/
  onClear?: (T: T) => void;
  /**Handle action on search*/
  onSearch?: (T: string) => void;
  /**Handle onKeyDown action*/
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onKeyDown?: (event: any) => void;
  /**Handle onEnter action*/
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onKeyEnter?: (event: any) => void;
  /**Handle the action when clear input search*/
  handleClearInput?: () => void;
  /**Use to custom style the component*/
  className?: string;
  /**Control the style type of component: MATERIAL, BORDERED, FLOAT_LABEL */
  type?: BORDER_TYPE;
  /**Label for current field*/
  label?: string;
  /** Show symbol * as required field */
  isRequired?: boolean;
  /**Control the size of the component*/
  isSmall?: boolean;
  /**True for data list of filter is Enum (not use in code)*/
  isEnumerable?: boolean;
  /**Boolean true if used for filter*/
  isFilter?: boolean;
  /** Provide a custom action (onClick) to the component */
  action?: InputAction;
  /** Custom background color for component: "white" || "gray" */
  bgColor?: "white" | "gray";
  /** If allow to remove input content with clear icon */
  allowClear?: boolean;
  /**Suffix for filter value*/
  suffix?: string | JSX.Element;

  readOnly?: boolean;
  /**Show tooltip */
  isShowTooltip?: boolean;
  /** handle onblur input */
  onBlur?: () => void;
  /** Handle the action when the label is clicked */
  handleClickLabel?: () => void;

  checkAllowClearWithRequire?: boolean;
}
function defaultRenderObject<T extends Model>(t: T) {
  return t?.name || "";
}

function InputSelect(props: InputSelectProps<Model>) {
  const {
    render = defaultRenderObject,
    expanded = false,
    disabled = false,
    value,
    placeHolder,
    handleClearInput: onClearInput,
    onClear,
    onSearch,
    onKeyDown,
    onKeyEnter,
    className,
    type,
    label,
    action,
    isRequired,
    isSmall = true,
    isEnumerable,
    isFilter,
    bgColor,
    handlePressExpandedIcon,
    allowClear = true,
    suffix,
    readOnly = false,
    isShowTooltip = true,
    onBlur,
    handleClickLabel,
    checkAllowClearWithRequire = true,
  } = props;

  const inputRef: RefObject<HTMLInputElement> =
    React.useRef<HTMLInputElement>(null);

  const [internalValue, setInternalValue] = React.useState("");

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInternalValue(event.target.value);
      if (typeof onSearch === "function") {
        onSearch(event.target.value);
      }
    },
    [onSearch]
  );

  const handleClearInput = React.useCallback(() => {
    setInternalValue("");
    inputRef.current.focus();

    if (typeof handleClearInput !== undefined) {
      onClearInput();
    }
  }, [onClearInput]);

  const handleClearItem = React.useCallback(
    (event: React.MouseEvent<ReactSVGElement, MouseEvent>) => {
      if (typeof onClear === "function") {
        onClear(null);
      }
      event.stopPropagation();
    },
    [onClear]
  );

  const handleKeyDown = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: any) => {
      if (typeof onKeyDown === "function") {
        onKeyDown(event);
      }
    },
    [onKeyDown]
  );

  const handleEnter = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: any) => {
      if (typeof onKeyEnter === "function") {
        onKeyEnter(event);
      }
    },
    [onKeyEnter]
  );

  const handleClickChevron = React.useCallback(
    (event: React.MouseEvent<ReactSVGElement, MouseEvent>) => {
      event.stopPropagation();
      handlePressExpandedIcon();
    },
    [handlePressExpandedIcon]
  );

  React.useEffect(() => {
    if (expanded) {
      setInternalValue("");
      inputRef.current.focus();
    }
  }, [expanded]);

  return (
    <>
      <div className={classNames("input-select__wrapper", className)}>
        {(label || action?.name) && (
          <div
            className="input-select__label m-b--3xs p-l--3xs"
            onClick={(event) => {
              handleClickLabel();
              event.stopPropagation();
            }}
          >
            {type !== BORDER_TYPE.FLOAT_LABEL && label && (
              <label
                className={classNames("component__title", {
                  "component__title--disabled": disabled,
                })}
              >
                {label}
                {isRequired && <span className="text-danger">&nbsp;*</span>}
              </label>
            )}
            {action && (
              <span
                className="m-l--3xs body-text--md color-link"
                style={{ cursor: "pointer" }}
                onClick={action.action}
              >
                {action.name}
              </span>
            )}
          </div>
        )}
        <div
          className={classNames(
            "component__input input-select__container p--xs",
            {
              "input-select__container--sm": isSmall,
              "input-select__container--white": bgColor === "white",
              "py--2xs": isSmall,
              "px--xs": isSmall,
              "p--xs": !isSmall,
              "input-select--material": type === BORDER_TYPE.MATERIAL,
              "input-select--disabled ": disabled,
              "input-select--float": type === BORDER_TYPE.FLOAT_LABEL,
              "input-select--bordered": type === BORDER_TYPE.BORDERED,
              "input-select--filter-have-item": isFilter && value,
              "input-select--readOnly": readOnly,
            }
          )}
        >
          {expanded ? (
            <>
              <input
                type="text"
                value={internalValue}
                onChange={handleChange}
                placeholder={
                  value
                    ? (render(value) as string)
                    : type === BORDER_TYPE.FLOAT_LABEL && label
                      ? " "
                      : placeHolder
                }
                onBlur={onBlur}
                ref={inputRef}
                disabled={disabled || readOnly}
                onKeyDown={handleKeyDown}
                className={classNames("component__input", {
                  "disabled-field": disabled,
                })}
                readOnly={isEnumerable}
              />
              {type === BORDER_TYPE.FLOAT_LABEL && label && (
                <label
                  className={classNames(
                    "component__title component__title--normal component__title--expanded",
                    {
                      "component__title--sm": isSmall,
                    }
                  )}
                >
                  {label}
                  {isRequired && <span className="text-danger">&nbsp;*</span>}
                </label>
              )}
              {allowClear && internalValue && !disabled ? (
                <div className={classNames("input-icon__clear", "m-x--2xs")}>
                  <Close onClick={handleClearInput} />
                </div>
              ) : null}

              {suffix && (
                <>
                  {typeof suffix === "string" ? (
                    <span className="m-l--2xs input-text__string">
                      {suffix}
                    </span>
                  ) : (
                    <div className="m-l--2xs input-text__icon">{suffix}</div>
                  )}
                </>
              )}
              <div style={{ display: "flex" }}>
                <ChevronDown
                  size={16}
                  className={classNames("input-icon", "input-select__icon", {
                    "input-select__icon--disabled": disabled,
                  })}
                  onClick={handleClickChevron}
                />
              </div>
            </>
          ) : (
            <>
              {isShowTooltip ? (
                <Tooltip
                  placement="topLeft"
                  title={(render(value) as string) || ""}
                >
                  <input
                    type="text"
                    value={(render(value) as string) || ""}
                    readOnly
                    placeholder={
                      type === BORDER_TYPE.FLOAT_LABEL && label
                        ? " "
                        : placeHolder
                    }
                    onKeyDown={handleEnter}
                    className={classNames("component__input", {
                      "disabled-field": disabled,
                    })}
                    disabled={disabled || readOnly}
                    ref={inputRef}
                  />
                </Tooltip>
              ) : (
                <input
                  type="text"
                  value={(render(value) as string) || ""}
                  readOnly
                  placeholder={
                    type === BORDER_TYPE.FLOAT_LABEL && label
                      ? " "
                      : placeHolder
                  }
                  onKeyDown={handleEnter}
                  className={classNames("component__input", {
                    "disabled-field": disabled,
                  })}
                  disabled={disabled || readOnly}
                  ref={inputRef}
                />
              )}

              {type === BORDER_TYPE.FLOAT_LABEL && label && (
                <label
                  className={classNames(
                    "component__title component__title--normal",
                    {
                      "component__title--sm": isSmall,
                    }
                  )}
                >
                  {label}
                  {isRequired && <span className="text-danger">&nbsp;*</span>}
                </label>
              )}
              {allowClear && value && !disabled && !readOnly && (!checkAllowClearWithRequire || !isRequired) && (
                <div className={classNames("input-icon__clear", "m-x--2xs")}>
                  <Close onClick={handleClearItem} />
                </div>
              )}
              {suffix && (
                <>
                  {typeof suffix === "string" ? (
                    <span className="p-l--2xs input-select__suffix m-r--3xs">
                      {suffix}
                    </span>
                  ) : (
                    <div className="p-l--2xs input-select__suffix">
                      {suffix}
                    </div>
                  )}
                </>
              )}
              <div style={{ display: "flex" }}>
                <ChevronDown
                  size={16}
                  className={classNames("input-icon", "input-select__icon", {
                    "input-select__icon--disabled": disabled || readOnly,
                  })}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default InputSelect;
